import dayjs, { Dayjs } from 'dayjs';

import { Game } from '../types';
import { getDateForCalendar } from './dates';

export type StatisticsPeriodType = 'week' | 'month' | 'year' | 'custom';

export type StatisticsPeriod = {
  type: StatisticsPeriodType;
  start: Date;
  end: Date;
};

export type StatusCounts = {
  confirmed: number;
  /** Waiting for the owner's answer; the game has not started yet. */
  pending: number;
  /** Never approved or declined by the owner before the game started. */
  unanswered: number;
  declined: number;
};

export type StatusKey = keyof StatusCounts;

export const STATUS_KEYS: StatusKey[] = [
  'confirmed',
  'pending',
  'unanswered',
  'declined',
];

export const emptyStatusCounts = (): StatusCounts => ({
  confirmed: 0,
  pending: 0,
  unanswered: 0,
  declined: 0,
});

export type BucketUnit = 'day' | 'month';

export type PlayerCounts = {
  /** Played on the field before the selected period. */
  regular: number;
  /** First game on the field is in the selected period. */
  new: number;
  /** Booked by the owner for someone without a Bolarena account. */
  unregistered: number;
};

export type PlayerCategory = keyof PlayerCounts;

export const PLAYER_CATEGORIES: PlayerCategory[] = [
  'regular',
  'new',
  'unregistered',
];

export type TimeBucket = StatusCounts &
  PlayerCounts & {
    key: string;
    date: Date;
    unit: BucketUnit;
  };

export type HeatmapCell = {
  weekday: number;
  hour: number;
  count: number;
};

export type DeclineReason = {
  key: string;
  count: number;
};

export type DeclineReasonOption = {
  key: string;
  /** The option's text in every app language, as saved by the owner. */
  texts: string[];
};

export type FieldStatistics = StatusCounts & {
  fieldId: number;
  name: string;
  total: number;
  players: number;
};

// Monday-first weekday index (0 = Monday ... 6 = Sunday)
const getWeekdayIndex = (date: Dayjs) => (date.day() + 6) % 7;

const getStartOfWeek = (date: Dayjs) =>
  date.subtract(getWeekdayIndex(date), 'day').startOf('day');

export const getPeriod = (
  type: Exclude<StatisticsPeriodType, 'custom'>,
  reference: Date
): StatisticsPeriod => {
  const ref = dayjs(reference);

  if (type === 'week') {
    const start = getStartOfWeek(ref);

    return {
      type,
      start: start.toDate(),
      end: start.add(6, 'day').endOf('day').toDate(),
    };
  }

  return {
    type,
    start: ref.startOf(type).toDate(),
    end: ref.endOf(type).toDate(),
  };
};

export const shiftPeriod = (
  period: StatisticsPeriod,
  direction: 1 | -1
): StatisticsPeriod => {
  if (period.type === 'custom') {
    return period;
  }

  const reference = dayjs(period.start)
    .add(direction, period.type)
    .toDate();

  return getPeriod(period.type, reference);
};

export const getGameStart = (game: Game) => {
  if (game.startWithDate) {
    return dayjs(getDateForCalendar(game.startWithDate));
  }

  const [hours = 0, minutes = 0] = (game.start || '')
    .split(':')
    .map(Number);

  return dayjs(game.date).hour(hours).minute(minutes);
};

export const getGameEnd = (game: Game) => {
  if (game.endWithDate) {
    return dayjs(getDateForCalendar(game.endWithDate));
  }

  const [hours = 0, minutes = 0] = (game.end || '').split(':').map(Number);

  return dayjs(game.date).hour(hours).minute(minutes);
};

export const isGameInPeriod = (game: Game, period: StatisticsPeriod) => {
  const start = getGameStart(game);

  return (
    !start.isBefore(dayjs(period.start)) && !start.isAfter(dayjs(period.end))
  );
};

/**
 * Unique identifiers of the people who played a game. Players without an
 * account (reservation made by the owner for another person) are identified
 * by their name and phone.
 */
export const getGamePlayerKeys = (game: Game): string[] => {
  const keys = new Set<string>();

  game.players?.forEach((player) => keys.add(`user:${player.id}`));

  const { reservation } = game;

  if (reservation?.otherUser) {
    keys.add(`user:${reservation.otherUser.id}`);
  } else if (reservation?.otherName) {
    keys.add(
      `other:${reservation.otherName.trim().toLowerCase()}:${
        reservation.otherPhone || ''
      }`
    );
  } else if (game.creator) {
    keys.add(`user:${game.creator.id}`);
  }

  return Array.from(keys);
};

export const isPlayedGame = (game: Game, now = dayjs()) =>
  game.status === 'CONFIRMED' && getGameEnd(game).isBefore(now);

export const getGameStatusKey = (game: Game, now = dayjs()): StatusKey => {
  if (game.status === 'CONFIRMED') {
    return 'confirmed';
  }

  if (game.status === 'DECLINED') {
    return 'declined';
  }

  return game.isExpired || getGameStart(game).isBefore(now)
    ? 'unanswered'
    : 'pending';
};

export const countStatuses = (games: Game[]): StatusCounts => {
  const now = dayjs();

  return games.reduce<StatusCounts>((acc, game) => {
    acc[getGameStatusKey(game, now)] += 1;

    return acc;
  }, emptyStatusCounts());
};

export const getStatusTotal = (counts: StatusCounts) =>
  STATUS_KEYS.reduce((sum, key) => sum + counts[key], 0);

export const countUniquePlayers = (games: Game[]) => {
  const now = dayjs();
  const players = new Set<string>();

  games
    .filter((game) => isPlayedGame(game, now))
    .forEach((game) =>
      getGamePlayerKeys(game).forEach((key) => players.add(key))
    );

  return players.size;
};

const getBucketUnit = (period: StatisticsPeriod): BucketUnit => {
  if (period.type === 'year') {
    return 'month';
  }

  if (period.type === 'custom') {
    const days = dayjs(period.end).diff(dayjs(period.start), 'day');

    return days > 62 ? 'month' : 'day';
  }

  return 'day';
};

const getPlayerCategory = (
  playerKey: string,
  knownPlayerKeys: Set<string>
): PlayerCategory => {
  if (playerKey.startsWith('other:')) {
    return 'unregistered';
  }

  return knownPlayerKeys.has(playerKey) ? 'regular' : 'new';
};

/** Player keys of all played games, e.g. from before the selected period. */
export const getPlayedPlayerKeys = (games: Game[]) => {
  const now = dayjs();
  const keys = new Set<string>();

  games
    .filter((game) => isPlayedGame(game, now))
    .forEach((game) => getGamePlayerKeys(game).forEach((key) => keys.add(key)));

  return keys;
};

/** Unique players of the played games, split by category. */
export const countPlayersByCategory = (
  games: Game[],
  knownPlayerKeys: Set<string>
): PlayerCounts => {
  const counts: PlayerCounts = { regular: 0, new: 0, unregistered: 0 };

  getPlayedPlayerKeys(games).forEach((key) => {
    counts[getPlayerCategory(key, knownPlayerKeys)] += 1;
  });

  return counts;
};

export const getTimeBuckets = (
  games: Game[],
  period: StatisticsPeriod,
  knownPlayerKeys: Set<string> = new Set()
): TimeBucket[] => {
  const unit = getBucketUnit(period);
  const keyFormat = unit === 'day' ? 'YYYY-MM-DD' : 'YYYY-MM';
  const buckets = new Map<string, TimeBucket & { playerKeys: Set<string> }>();

  let cursor = dayjs(period.start).startOf(unit);
  const end = dayjs(period.end);

  while (!cursor.isAfter(end)) {
    const key = cursor.format(keyFormat);

    buckets.set(key, {
      key,
      date: cursor.toDate(),
      unit,
      ...emptyStatusCounts(),
      regular: 0,
      new: 0,
      unregistered: 0,
      playerKeys: new Set(),
    });

    cursor = cursor.add(1, unit);
  }

  const now = dayjs();

  games.forEach((game) => {
    const bucket = buckets.get(getGameStart(game).format(keyFormat));

    if (!bucket) {
      return;
    }

    bucket[getGameStatusKey(game, now)] += 1;

    if (isPlayedGame(game, now)) {
      getGamePlayerKeys(game).forEach((key) => bucket.playerKeys.add(key));
    }
  });

  return Array.from(buckets.values()).map(({ playerKeys, ...bucket }) => {
    playerKeys.forEach((key) => {
      bucket[getPlayerCategory(key, knownPlayerKeys)] += 1;
    });

    return bucket;
  });
};

const parseHour = (time?: string) => {
  const hour = Number((time || '').split(':')[0]);

  return Number.isFinite(hour) ? hour : null;
};

/**
 * Counts how many active (confirmed or still pending) games occupy each
 * weekday/hour slot. A game from 18:00 to 20:00 occupies 18 and 19.
 * Declined and unanswered games are left out.
 */
export const getBusyHours = (
  games: Game[],
  openingHours?: string,
  closingHours?: string
) => {
  const counts = new Map<string, number>();
  let minHour = parseHour(openingHours) ?? 24;
  let maxHour = (parseHour(closingHours) ?? 0) - 1;
  const now = dayjs();

  games
    .filter((game) =>
      ['confirmed', 'pending'].includes(getGameStatusKey(game, now))
    )
    .forEach((game) => {
      const start = getGameStart(game);
      const end = getGameEnd(game);
      const weekday = getWeekdayIndex(start);

      let slot = start.startOf('hour');

      while (slot.isBefore(end)) {
        const hour = slot.hour();
        const key = `${weekday}-${hour}`;

        counts.set(key, (counts.get(key) || 0) + 1);
        minHour = Math.min(minHour, hour);
        maxHour = Math.max(maxHour, hour);

        slot = slot.add(1, 'hour');
      }
    });

  if (minHour > maxHour) {
    minHour = 8;
    maxHour = 22;
  }

  const hours = Array.from(
    { length: maxHour - minHour + 1 },
    (_, i) => minHour + i
  );

  const cells: HeatmapCell[] = [];

  for (let weekday = 0; weekday < 7; weekday += 1) {
    hours.forEach((hour) =>
      cells.push({
        weekday,
        hour,
        count: counts.get(`${weekday}-${hour}`) || 0,
      })
    );
  }

  return { hours, cells };
};

export const OTHER_REASONS_KEY = '__other__';

const normalizeReason = (text: string) => text.trim().toLowerCase();

/**
 * Counts declined games per predefined decline reason. Reasons that were
 * typed manually (or left empty) are counted under OTHER_REASONS_KEY.
 */
export const getDeclineReasons = (
  games: Game[],
  options: DeclineReasonOption[]
): DeclineReason[] => {
  const counts = new Map<string, number>();
  const optionByText = new Map<string, string>();

  options.forEach((option) =>
    option.texts.forEach((text) =>
      optionByText.set(normalizeReason(text), option.key)
    )
  );

  games
    .filter((game) => game.status === 'DECLINED')
    .forEach((game) => {
      const text = game.declineReason || game.reservation?.declineReason || '';
      const key = optionByText.get(normalizeReason(text)) || OTHER_REASONS_KEY;

      counts.set(key, (counts.get(key) || 0) + 1);
    });

  return [...options.map((option) => option.key), OTHER_REASONS_KEY].map(
    (key) => ({ key, count: counts.get(key) || 0 })
  );
};

export const getFieldsStatistics = (
  games: Game[],
  fields: { id: number; name: string }[]
): FieldStatistics[] => {
  const now = dayjs();
  const byField = new Map<
    number,
    FieldStatistics & { playerKeys: Set<string> }
  >();

  fields.forEach((field) =>
    byField.set(field.id, {
      fieldId: field.id,
      name: field.name,
      ...emptyStatusCounts(),
      total: 0,
      players: 0,
      playerKeys: new Set(),
    })
  );

  games.forEach((game) => {
    if (!game.field) {
      return;
    }

    let stats = byField.get(game.field.id);

    if (!stats) {
      stats = {
        fieldId: game.field.id,
        name: game.field.name,
        ...emptyStatusCounts(),
        total: 0,
        players: 0,
        playerKeys: new Set(),
      };
      byField.set(game.field.id, stats);
    }

    stats.total += 1;
    stats[getGameStatusKey(game, now)] += 1;

    if (isPlayedGame(game, now)) {
      getGamePlayerKeys(game).forEach((key) => stats!.playerKeys.add(key));
    }
  });

  return Array.from(byField.values())
    .map(({ playerKeys, ...stats }) => ({
      ...stats,
      players: playerKeys.size,
    }))
    .sort((a, b) => b.total - a.total);
};

export type BucketWeather = {
  /** Average daily temperature, °C. */
  temperature: number | null;
  /** Total rain, mm. */
  rain: number | null;
};

/**
 * Weather per time bucket: the average temperature and the total rain of
 * the bucket's days.
 */
export const getBucketWeather = (
  buckets: TimeBucket[],
  daily: Map<string, { temperature: number | null; rain: number | null }>
): Map<string, BucketWeather> => {
  const result = new Map<string, BucketWeather>();

  buckets.forEach((bucket) => {
    const days =
      bucket.unit === 'day'
        ? [daily.get(bucket.key)]
        : Array.from(daily.entries())
            .filter(([date]) => date.startsWith(bucket.key))
            .map(([, weather]) => weather);

    const temperatures = days
      .map((day) => day?.temperature)
      .filter((value): value is number => typeof value === 'number');
    const rains = days
      .map((day) => day?.rain)
      .filter((value): value is number => typeof value === 'number');

    result.set(bucket.key, {
      temperature: temperatures.length
        ? temperatures.reduce((sum, value) => sum + value, 0) /
          temperatures.length
        : null,
      rain: rains.length ? rains.reduce((sum, value) => sum + value, 0) : null,
    });
  });

  return result;
};
